import { useCallback, useLayoutEffect, useRef } from 'react'

/**
 * Returns a function whose identity never changes but which always calls the latest `callback`.
 *
 * For a callback that is handed to many consumers through a context value: a plain function
 * declared in the provider gets a new identity on every render, which makes the context value
 * change (and every consumer re-render) on renders that have nothing to do with it, such as a
 * scroll frame. The wrapper is for handlers that run in response to events, not during render.
 */
export const useStableCallback = <Args extends unknown[], Result>(
  callback: (...args: Args) => Result
): ((...args: Args) => Result) => {
  const latest = useRef(callback)
  useLayoutEffect(() => {
    latest.current = callback
  })
  return useCallback((...args: Args): Result => latest.current(...args), [])
}
